import { Helmet } from 'react-helmet-async';
import { usePageSeoContext } from '../lib/PageSeoContext';
import { resolveMediaUrl } from '../lib/mediaApi';
import { getSiteOrigin } from '../lib/siteUrl';
import { breadcrumbSchema, breadcrumbsFor, webPageSchema } from '../lib/jsonLd';

interface SeoProps {
  pageKey: string;
  fallbackTitle: string;
  fallbackDescription: string;
  jsonLd?: object | object[];
}

export default function Seo({ pageKey, fallbackTitle, fallbackDescription, jsonLd }: SeoProps) {
  const { pages, siteSettings } = usePageSeoContext();
  const seo = pages[pageKey];

  const title = seo?.meta_title || fallbackTitle;
  const description = seo?.meta_description || fallbackDescription;
  const keywords = seo?.meta_keywords || '';
  const siteName = siteSettings.seo_site_name || 'Homeland Stay';
  const twitterHandle = siteSettings.seo_twitter_handle || '';
  const ogImageRaw = seo?.og_image_url || siteSettings.seo_default_og_image || '';
  const ogImage = ogImageRaw ? resolveMediaUrl(ogImageRaw) : '';
  const ogImageAlt = seo?.og_image_alt || title;

  const origin = getSiteOrigin();
  const canonicalPath = seo?.canonical_path || (typeof window !== 'undefined' ? window.location.pathname : '');
  const canonicalUrl = `${origin}${canonicalPath}`;

  // Every page also states what it is (WebPage) and where it sits (breadcrumbs),
  // on top of any page-specific schema the caller passes in.
  const pathForCrumbs = canonicalPath.replace(/\/$/, '');
  // Breadcrumb names use the page name, not the "— Site name" title suffix.
  const pageName = title.split(/\s[—|]\s/)[0];
  const crumbs = breadcrumbsFor(origin, pathForCrumbs, pageName);
  const generatedSchema: object[] = [
    webPageSchema({ origin, url: canonicalUrl, name: title, description }),
    ...(crumbs.length > 0 ? [breadcrumbSchema(crumbs)] : []),
  ];
  const jsonLdList = [...generatedSchema, ...(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [])];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={seo?.robots_noindex ? 'noindex, nofollow' : 'index, follow'} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:alt" content={ogImageAlt} />}

      <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {ogImage && <meta name="twitter:image:alt" content={ogImageAlt} />}
      {twitterHandle && <meta name="twitter:site" content={twitterHandle} />}

      {jsonLdList.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
